import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SyncStatus } from "@/components/SyncStatus";
import { ConferenceTable, VIEWS } from "@/components/standings/ConferenceTable";
import { StandingsSidebar } from "@/components/standings/StandingsSidebar";
import "@/components/standings/standings.css";

const CONFS = [["Eastern Conference", "east"], ["Western Conference", "west"]];
const updated = (iso) => iso && new Date(iso).toLocaleString("en-GB", { timeZone: "Europe/Athens", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function Standings() {
  const [season, setSeason] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [view, setView] = useState("standings");
  const [conf, setConf] = useState("all");
  const [search, setSearch] = useState("");
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);

  const refreshNow = async () => {
    setSyncing(true);
    const before = data?.last_sync;
    try {
      await api.post("/teams/sync");
      toast.success("Ξεκίνησε η ανανέωση δεδομένων…");
      for (let i = 0; i < 24; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const r = await api.get("/standings", { params: season ? { season } : {} });
        setData(r.data);
        if (r.data.last_sync && r.data.last_sync !== before) { toast.success("Τα δεδομένα ανανεώθηκαν"); break; }
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    setError("");
    setData((d) => (d && season && d.season !== season ? null : d));
    api.get("/standings", { params: season ? { season } : {} }).then((r) => setData(r.data)).catch((e) => setError(errorText(e)));
  }, [season]);

  const label = data?.seasons?.find((s) => s.value === data.season)?.label || "";
  const q = search.trim().toLowerCase();
  const rows = (data?.rows || []).filter((r) => !q || `${r.full_name} ${r.code}`.toLowerCase().includes(q));
  return (
    <div className="ncs-root" data-testid="standings-page">
      <header className="ncs-heading">
        <div>
          <div className="ncs-eyebrow">NBA / STANDINGS</div>
          <h1 data-testid="standings-title">NBA STANDINGS <span>{label}</span></h1>
          <p data-testid="standings-updated">Regular Season{data?.current && data.last_sync ? ` · Updated ${updated(data.last_sync)} (Athens)` : data && !data.current ? " · Final" : ""}</p>
          {data?.current && <div style={{ marginTop: 8 }}>
            <SyncStatus lastSync={data.last_sync} onRefresh={refreshNow} busy={syncing} canRefresh={user?.role === "admin"} testid="standings-sync" />
          </div>}
        </div>
        <div className="ncs-controls">
          <label>SEASON
            <select value={data?.season || ""} onChange={(e) => setSeason(Number(e.target.value))} data-testid="standings-season-select">
              {(data?.seasons || []).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>
          <label>CONFERENCE
            <select value={conf} onChange={(e) => setConf(e.target.value)} data-testid="standings-conf-select">
              <option value="all">Both</option><option value="east">Eastern</option><option value="west">Western</option>
            </select>
          </label>
        </div>
      </header>
      <nav className="ncs-tabs" aria-label="Standings view" data-testid="standings-tabs">
        {VIEWS.map(([key, text]) => (
          <button key={key} type="button" className={view === key ? "active" : ""} aria-pressed={view === key} onClick={() => setView(key)} data-testid={`standings-tab-${key}`}>{text}</button>
        ))}
      </nav>
      <div className="ncs-layout">
        <section className="ncs-main">
          <label className="ncs-search">Search team
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Team name or code" data-testid="standings-search" />
          </label>
          {error && <p className="ncs-state" role="alert" data-testid="standings-error">{error}</p>}
          {!data && !error && <p className="ncs-state" role="status" data-testid="standings-loading">Loading standings…</p>}
          {data && !data.rows.length && <p className="ncs-state" data-testid="standings-empty">Standings are not synced yet.</p>}
          {data && CONFS.filter(([, k]) => conf === "all" || conf === k).map(([name, k]) => (
            <ConferenceTable key={k} id={k} title={name} rows={rows.filter((r) => r.conference === name)} view={view} />
          ))}
          {data && data.rows.length > 0 && !rows.length && <p className="ncs-state" data-testid="standings-no-match">No matching teams.</p>}
          <p className="ncs-footnote">Order, seeds and records are the official ESPN standings (NBA tie-breakers applied). GB is measured from the conference leader. Synced at 09:00 and 18:00 Athens.</p>
        </section>
        {data && <StandingsSidebar data={data} />}
      </div>
    </div>
  );
}
